import { ref, computed } from "vue";
import { statisticsService, type Statistics } from "@/services/statisticsService";
import { logDebug, logInfo, logError } from "@/utils/secureLogger";

const statisticsState = ref<Statistics | null>(null);
const isLoadingState = ref(false);
const errorMessageState = ref<string | null>(null);
const lastUpdatedState = ref<number | null>(null);

interface LoadOptions {
  force?: boolean;
}

const loadStatistics = async (options: LoadOptions = {}): Promise<Statistics | null> => {
  const { force = false } = options;

  if (isLoadingState.value) {
    logDebug("useStatistics: load already in progress");
    return statisticsState.value;
  }

  if (statisticsState.value && !force) {
    logDebug("useStatistics: returning memoized statistics");
    return statisticsState.value;
  }

  try {
    isLoadingState.value = true;
    errorMessageState.value = null;

    // If force=true, clear the service cache to ensure fresh data from database
    if (force) {
      statisticsService.clearCache();
      logDebug("useStatistics: cache cleared, fetching fresh data");
    }

    const stats = await statisticsService.getCachedStatistics();
    statisticsState.value = stats;
    lastUpdatedState.value = Date.now();

    logInfo("useStatistics: statistics updated", stats);
    return stats;
  } catch (error) {
    errorMessageState.value = "Unable to load statistics at this time.";
    logError("useStatistics: failed to load statistics", error);
    return null;
  } finally {
    isLoadingState.value = false;
  }
};

export function useStatistics() {
  return {
    statistics: computed(() => statisticsState.value),
    isLoading: computed(() => isLoadingState.value),
    lastUpdated: computed(() => lastUpdatedState.value),
    errorMessage: computed(() => errorMessageState.value),
    loadStatistics,
    refreshStatistics: () => loadStatistics({ force: true }),
  };
}
